import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportText } from '../utils/reportTranslation';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import type { ParentInfo as ParentInfoType } from '../types';

interface ParentInfoProps {
  parentInfo: ParentInfoType;
  communityTypeOnly?: boolean;
}

/**
 * Parent contact information display component
 */
const ParentInfo: React.FC<ParentInfoProps> = ({ parentInfo, communityTypeOnly = false }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyFallback = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    } finally {
      document.body.removeChild(textArea);
    }
    return ok;
  };

  // Check if values are masked (****) - don't make them clickable
  const isMasked = (value: string) => value === '****';

  const safeEmail = parentInfo.email;
  const safePhone = parentInfo.phone;

  const handleCopyEmail = async () => {
    const email = safeEmail || '';
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email);
        ok = true;
      } else {
        ok = copyFallback(email);
      }
    } catch {
      ok = copyFallback(email);
    }
    setCopied(ok);
    window.setTimeout(() => setCopied(false), 1600);
  };

  // Check if parent is a research participant
  const isResearchParticipant = parentInfo.researchId !== undefined && parentInfo.researchId !== null && parentInfo.researchId !== '';

  return (
    <section id="caregiver-details" className="report-section">
      <div className="report-card__header">
        <div>
          <p className="report-eyebrow">{t('parentInfo.eyebrow')}</p>
          <h3 className="report-card__title">{t('parentInfo.title')}</h3>
        </div>
      </div>

      <div className="report-info-list">
        {!communityTypeOnly && isResearchParticipant && (
          <div className="report-info-row">
            <FaUser className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.researchId')}</p>
              <p className="report-info-value">{parentInfo.researchId}</p>
            </div>
          </div>
        )}

        {!communityTypeOnly && (
          <div className="report-info-row">
            <FaUser className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.name')}</p>
              <p className="report-info-value">{parentInfo.name}</p>
            </div>
          </div>
        )}

        {!communityTypeOnly && parentInfo.relationship && (
          <div className="report-info-row">
            <FaUser className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.relationship')}</p>
              <p className="report-info-value">{parentInfo.relationship}</p>
            </div>
          </div>
        )}

        {!communityTypeOnly && (
          <div className="report-info-row">
            <FaEnvelope className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.email')}</p>
              <div className="flex min-w-0 items-start gap-2">
                {isMasked(safeEmail) ? (
                  <p className="report-info-value">{safeEmail}</p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className="report-copy-link report-info-value min-w-0 text-left"
                      title={t('parentInfo.copyEmailTitle')}
                      aria-label={t('parentInfo.copyEmailAria')}
                    >
                      {safeEmail}
                    </button>
                    {copied && (
                      <span className="text-xs text-[var(--green-text)] shrink-0 pt-1" aria-live="polite">
                        {t('common.copied')}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {!communityTypeOnly && (
          <div className="report-info-row">
            <FaPhone className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.phone')}</p>
              {isMasked(safePhone) ? (
                <p className="report-info-value">{safePhone}</p>
              ) : (
                <a
                  href={`tel:${safePhone}`}
                  className="report-copy-link report-info-value"
                >
                  {safePhone}
                </a>
              )}
            </div>
          </div>
        )}

        {!communityTypeOnly && (
          <div className="report-info-row">
            <FaMapMarkerAlt className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.location')}</p>
              <p className="report-info-value">{parentInfo.location}</p>
            </div>
          </div>
        )}

        {parentInfo.communityType && (
          <div className="report-info-row">
            <FaMapMarkerAlt className="report-info-icon" />
            <div className="min-w-0">
              <p className="report-info-label">{t('parentInfo.communityType')}</p>
              <p className="report-info-value">{reportText(parentInfo.communityType)}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ParentInfo;
